import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Check, X, Clock, Mail, Phone, Briefcase, MessageSquare, RefreshCw, ChevronDown, ChevronUp, Trash2, AlertCircle } from 'lucide-react';
import { API_URL } from '../config';

export default function InscriptionRequests({ authToken, authUser, apiCall }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('en_attente');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [services, setServices] = useState([]);
  const [approveData, setApproveData] = useState({});
  const [rejectComment, setRejectComment] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/registration-requests');
      setRequests(data);
    } catch (err) {
      console.error('Erreur chargement demandes:', err);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const loadServices = useCallback(async () => {
    try {
      const data = await apiCall('/services');
      setServices(data);
    } catch (err) {
      console.error('Erreur chargement services:', err);
    }
  }, [apiCall]);

  useEffect(() => {
    loadRequests();
    loadServices();
  }, [loadRequests, loadServices]);

  const handleApprove = async (request) => {
    setActionLoading(request.id);
    try {
      const serviceId = approveData[request.id]?.service_id;
      await apiCall(`/registration-requests/${request.id}`, 'PUT', {
        statut: 'accepte',
        service_id: serviceId || undefined
      });
      setConfirmAction(null);
      setExpandedId(null);
      await loadRequests();
    } catch (err) {
      console.error('Erreur approbation:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (request) => {
    setActionLoading(request.id);
    try {
      await apiCall(`/registration-requests/${request.id}`, 'PUT', {
        statut: 'refuse',
        commentaire_admin: rejectComment[request.id] || undefined
      });
      setConfirmAction(null);
      setExpandedId(null);
      await loadRequests();
    } catch (err) {
      console.error('Erreur refus:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    setActionLoading(id);
    try {
      await apiCall(`/registration-requests/${id}`, 'DELETE');
      await loadRequests();
    } catch (err) {
      console.error('Erreur suppression:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredRequests = requests.filter(r =>
    filter === 'en_attente' ? r.statut === 'en_attente' : r.statut !== 'en_attente'
  );

  const pendingCount = requests.filter(r => r.statut === 'en_attente').length;

  const statusBadge = (statut) => {
    switch (statut) {
      case 'en_attente':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200"><Clock className="w-3 h-3" /> En attente</span>;
      case 'accepte':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200"><Check className="w-3 h-3" /> Approuvée</span>;
      case 'refuse':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200"><X className="w-3 h-3" /> Refusée</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-text-primary">Demandes d'inscription</h2>
            <p className="text-sm text-text-muted">{pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadRequests}
            className="p-2 text-text-muted hover:text-purple-600 hover:bg-purple-50 dark:bg-purple-950/40 rounded-lg transition"
            title="Actualiser"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2">
        <button
          onClick={() => setFilter('en_attente')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            filter === 'en_attente'
              ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 shadow-sm'
              : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
          }`}
        >
          En attente {pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-white rounded-full text-xs">{pendingCount}</span>}
        </button>
        <button
          onClick={() => setFilter('traitees')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
            filter === 'traitees'
              ? 'bg-gray-700 text-white shadow-sm'
              : 'bg-elevated-1 text-text-secondary hover:bg-elevated-1'
          }`}
        >
          Traitées
        </button>
      </div>

      {/* Liste des demandes */}
      {filteredRequests.length === 0 ? (
        <div className="text-center py-12 bg-surface rounded-xl border border-border-subtle">
          <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-text-muted">
            {filter === 'en_attente' ? 'Aucune demande en attente' : 'Aucune demande traitée'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRequests.map(request => (
            <div key={request.id} className={`bg-surface rounded-xl border shadow-sm overflow-hidden transition ${
              request.statut === 'en_attente' ? 'border-amber-200 dark:border-amber-800' : 'border-border-subtle'
            }`}>
              {/* Ligne principale */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-elevated-1 transition"
                onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {(request.first_name[0] + request.last_name[0]).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-text-primary truncate">{request.first_name} {request.last_name}</p>
                    <div className="flex items-center gap-3 text-sm text-text-muted">
                      <span className="flex items-center gap-1 truncate"><Mail className="w-3.5 h-3.5" /> {request.email}</span>
                      <span className="hidden sm:flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {request.phone}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200">
                    <Briefcase className="w-3 h-3" /> {request.desired_role}
                  </span>
                  {statusBadge(request.statut)}
                  {expandedId === request.id ? <ChevronUp className="w-5 h-5 text-text-muted" /> : <ChevronDown className="w-5 h-5 text-text-muted" />}
                </div>
              </div>

              {/* Détail étendu */}
              {expandedId === request.id && (
                <div className="border-t border-border-subtle p-4 bg-elevated-1 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-text-muted">Email :</span> <span className="font-medium">{request.email}</span></div>
                    <div><span className="text-text-muted">Téléphone :</span> <span className="font-medium">{request.phone}</span></div>
                    <div><span className="text-text-muted">Rôle souhaité :</span> <span className="font-medium">{request.desired_role}</span></div>
                    <div><span className="text-text-muted">Date :</span> <span className="font-medium">{new Date(request.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></div>
                  </div>

                  {request.message && (
                    <div className="p-3 bg-surface rounded-lg border border-border-subtle">
                      <div className="flex items-center gap-2 text-sm text-text-muted mb-1">
                        <MessageSquare className="w-4 h-4" /> Message
                      </div>
                      <p className="text-sm text-text-secondary">{request.message}</p>
                    </div>
                  )}

                  {/* Info admin (pour demandes traitées) */}
                  {request.statut !== 'en_attente' && (
                    <div className="p-3 bg-surface rounded-lg border border-border-subtle text-sm">
                      <p><span className="text-text-muted">Traitée par :</span> <span className="font-medium">{request.admin_first_name} {request.admin_last_name}</span></p>
                      {request.traite_le && <p><span className="text-text-muted">Le :</span> <span className="font-medium">{new Date(request.traite_le).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></p>}
                      {request.commentaire_admin && <p className="mt-1"><span className="text-text-muted">Commentaire :</span> <span className="font-medium">{request.commentaire_admin}</span></p>}
                    </div>
                  )}

                  {/* Actions pour demandes en attente */}
                  {request.statut === 'en_attente' && (
                    <div className="space-y-3">
                      {/* Confirmation d'approbation */}
                      {confirmAction === `approve-${request.id}` ? (
                        <div className="p-4 bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 rounded-lg space-y-3">
                          <p className="text-sm font-medium text-green-800">Confirmer l'approbation</p>

                          {request.desired_role !== 'Extra' && (
                            <div>
                              <label className="block text-sm text-green-700 dark:text-green-300 mb-1">Service</label>
                              <select
                                value={approveData[request.id]?.service_id || ''}
                                onChange={(e) => setApproveData(prev => ({ ...prev, [request.id]: { ...prev[request.id], service_id: e.target.value ? parseInt(e.target.value) : undefined } }))}
                                className="w-full px-3 py-2 border border-green-300 dark:border-green-700 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              >
                                <option value="">Service par défaut</option>
                                {services.filter(s => s.name !== 'Extra').map(s => (
                                  <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleApprove(request)}
                              disabled={actionLoading === request.id}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                              {actionLoading === request.id ? 'Traitement...' : 'Confirmer'}
                            </button>
                            <button
                              onClick={() => setConfirmAction(null)}
                              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-text-secondary rounded-lg text-sm font-medium transition"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : confirmAction === `reject-${request.id}` ? (
                        <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-lg space-y-3">
                          <p className="text-sm font-medium text-red-800 dark:text-red-200">Confirmer le refus</p>
                          <textarea
                            value={rejectComment[request.id] || ''}
                            onChange={(e) => setRejectComment(prev => ({ ...prev, [request.id]: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                            placeholder="Motif du refus (optionnel)..."
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReject(request)}
                              disabled={actionLoading === request.id}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                              {actionLoading === request.id ? 'Traitement...' : 'Refuser'}
                            </button>
                            <button
                              onClick={() => setConfirmAction(null)}
                              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-text-secondary rounded-lg text-sm font-medium transition"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => setConfirmAction(`approve-${request.id}`)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition"
                          >
                            <Check className="w-4 h-4" /> Approuver
                          </button>
                          <button
                            onClick={() => setConfirmAction(`reject-${request.id}`)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition"
                          >
                            <X className="w-4 h-4" /> Refuser
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bouton supprimer pour demandes traitées */}
                  {request.statut !== 'en_attente' && (
                    <button
                      onClick={() => handleDelete(request.id)}
                      disabled={actionLoading === request.id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:bg-red-950/40 rounded-lg transition disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" /> Supprimer
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
